import React, { /* useEffect, */ type FC } from 'react'
import Answers from './Answers'
import Question from './Question'
import { AnimatePresence, motion } from 'framer-motion'
import Cover from './Cover'
import Breadcrumb from './Breadcrumb'
import Menu from './Menu'
import Navigation from './Navigation'
import Explanation from './Explanation'
import { SHOW_BREADCRUMB } from '../configs'

interface IProps {
  index: number
  data: IQuiz
  goToPreviousQuestion: () => void
  goToNextQuestion: () => void
  goToQuestion: (index: number) => void
  userAnswers: IUserAnswer[]
  saveUserAnswer: (answer: IAnswer) => void
  lastIndex: number
  timer: ITimer
  sendResults: () => void
  quizId: string
}

const Quiz: FC<IProps> = ({
  index,
  data,
  goToPreviousQuestion,
  goToNextQuestion,
  goToQuestion,
  userAnswers,
  saveUserAnswer,
  lastIndex,
  timer,
  sendResults,
  quizId
}) => {
  const { questions, tooltip } = data.acf_fields

  const isPreviousQuestion = index !== 0
  const isNextQuestion = index !== questions.length - 1

  const question = questions[index]
  const { id, label, cover, type, answers, explanation } = question

  const userAnswer = userAnswers[index].answers
  const answersRequired = question.answers.reduce((acc: number, answer: IAnswer) => {
    if (answer.isCorrect) acc += 1
    return acc
  }, 0)
  const answersLeft = answersRequired - userAnswer.length

  return (
    <div className='h-screen w-screen flex flex-col overflow-auto lg:overflow-hidden'>
      <Menu
        tooltip={tooltip}
        minutes={timer.minutes}
        seconds={timer.seconds}
        quizId={quizId}
      />
      <AnimatePresence mode='wait'>
        <motion.div
          key={id}
          className='flex-1 grid grid-cols-1 lg:grid-cols-2 lg:overflow-auto'
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{
            delay: 0.1,
            type: 'spring',
            stiffness: 120,
            damping: 10,
            duration: 0.5,
            bounce: 0.2,
            mass: 0.5,
            velocity: 0,
          }}
        >
          <Cover
            cover={cover}
            index={index}
            questionsLength={questions.length}
            minutes={timer.minutes}
            seconds={timer.seconds}
          />
          <div className='w-full flex flex-col justify-center gap-8 max-w-3xl mx-auto px-8 lg:overflow-auto'>
            <Question
              index={index}
              label={label}
            />
            <Answers
              type={type}
              answers={answers}
              userAnswer={userAnswer}
              saveUserAnswer={saveUserAnswer}
              answersLeft={answersLeft}
            >
              <Explanation
                answersLeft={answersLeft}
                answersRequired={answersRequired}
                explanation={explanation}
              />
            </Answers>
            <Navigation
              goToPreviousQuestion={goToPreviousQuestion}
              goToNextQuestion={goToNextQuestion}
              isPreviousQuestion={isPreviousQuestion}
              isNextQuestion={isNextQuestion}
              answersLeft={answersLeft}
              sendResults={sendResults}
            />
          </div>
        </motion.div>
      </AnimatePresence>
      {SHOW_BREADCRUMB && (
        <Breadcrumb
          lastIndex={lastIndex}
          userAnswers={userAnswers}
          goToQuestion={goToQuestion}
        />
      )}
    </div>
  )
}

export default Quiz
